/**
 * Compact read-only table used inside the revert / restore preview modals.
 *
 * Renders the integrated `DataList` in **inline** (client) mode — no network
 * request — from a pre-stringified rows array. Used to display a version diff
 * (field / before / after) and a restore report (entity / outcome). Only the
 * `DataList.Table` is rendered (no toolbar/footer); `pageSize` is large so the
 * whole preview shows on one page.
 */

import { useMemo } from 'react';

import type { DataListConfig } from '@components/integrated/data-list';
import { DataList, useDataList } from '@components/integrated/data-list';

export interface PreviewColumn {
  /** Stable column key (also the key read from each row record). */
  key: string;
  /** Display header label. */
  label: string;
}

export interface PreviewTableProps {
  /** Unique id for this table instance. */
  id: string;
  columns: PreviewColumn[];
  rows: Array<Record<string, string>>;
  /** Message shown when `rows` is empty. */
  emptyMessage?: string;
}

export function PreviewTable({
  id,
  columns,
  rows,
  emptyMessage = 'Aucun élément',
}: Readonly<PreviewTableProps>) {
  const config = useMemo<DataListConfig>(
    () => ({
      id,
      dataSource: { data: rows },
      columns: Object.fromEntries(
        columns.map((column, index) => [
          column.key,
          { label: column.label, order: index + 1, type: 'text' as const },
        ]),
      ),
      i18n: { emptyMessage },
      sorting: { enabled: false },
      pagination: {
        mode: 'pagination',
        pageSize: 1000,
        showPageSize: false,
        showTotal: false,
      },
    }),
    [id, columns, rows, emptyMessage],
  );

  const list = useDataList(config);

  return (
    <DataList config={config} list={list}>
      <DataList.Table />
    </DataList>
  );
}
